/**
 * TypeScript mirror of the backend's platform event catalog
 * (``roboto.domain.triggers.DEFAULT_PLATFORM_EVENT_CATALOG``).
 *
 * Hand-maintained; ``eventsCatalog.parity.test.ts`` cross-checks it against
 * ``packages/roboto/src/roboto/domain/triggers/event_catalog.json``, which a
 * pytest check keeps current against the real catalog. Drift fails one of the
 * two tests, naming the event type that moved.
 *
 * This is what keeps the UI honest about which events exist, which namespace
 * roots a condition may reference, and which ``once_per`` granularities are
 * legal for a given event subscription.
 */

import { OncePer, PlatformEventType } from "./TriggerV2Record";

/**
 * The kind of thing a platform event is about: the ``roboto://`` type name of
 * its subject. Mirrors the manifest's ``subject_type``; the catalog is the one
 * statement of it, so nothing here parses the type string.
 */
export type SubjectType =
  | "dataset"
  | "file"
  | "invocation"
  | "event"
  | "trigger";

/** One platform event type's catalog entry. */
export interface PlatformEventDescriptor {
  /** Human label for pickers, e.g. "File uploaded". */
  label: string;
  /** What the occurrence is about. */
  subjectType: SubjectType;
  /** Namespace roots this event exposes to conditions and target templates. */
  roots: readonly string[];
  /** Root that unqualified condition fields bind to. */
  defaultRoot: string;
  /** Dedup granularities this event type supports. */
  oncePer: readonly OncePer[];
  /**
   * Whether a trigger may subscribe to this type. False for the schedule
   * occurrence, which the scheduler addresses to one trigger rather than
   * broadcasts — it still has a descriptor so templates know its roots.
   */
  subscribable: boolean;
}

export const EVENT_CATALOG: Record<PlatformEventType, PlatformEventDescriptor> =
  {
    [PlatformEventType.FileUploaded]: {
      label: "File uploaded",
      subjectType: "file",
      roots: ["dataset", "file"],
      defaultRoot: "dataset",
      oncePer: [OncePer.Dataset, OncePer.Occurrence, OncePer.File],
      subscribable: true,
    },
    [PlatformEventType.FileIngested]: {
      label: "File ingested",
      subjectType: "file",
      roots: ["dataset", "file"],
      defaultRoot: "dataset",
      oncePer: [OncePer.Dataset, OncePer.Occurrence, OncePer.File],
      subscribable: true,
    },
    [PlatformEventType.FileMetadataUpdated]: {
      label: "File metadata updated",
      subjectType: "file",
      roots: ["changed", "dataset", "file", "tag"],
      defaultRoot: "dataset",
      oncePer: [OncePer.Dataset, OncePer.Occurrence, OncePer.File],
      subscribable: true,
    },
    [PlatformEventType.DatasetCreated]: {
      label: "Dataset created",
      subjectType: "dataset",
      roots: ["dataset"],
      defaultRoot: "dataset",
      oncePer: [OncePer.Dataset, OncePer.Occurrence],
      subscribable: true,
    },
    [PlatformEventType.UploadCompleted]: {
      label: "Dataset upload completed",
      subjectType: "dataset",
      roots: ["dataset", "upload"],
      defaultRoot: "dataset",
      oncePer: [OncePer.Dataset, OncePer.Occurrence],
      subscribable: true,
    },
    [PlatformEventType.DatasetMetadataUpdated]: {
      label: "Dataset metadata updated",
      subjectType: "dataset",
      roots: ["changed", "dataset", "tag"],
      defaultRoot: "dataset",
      oncePer: [OncePer.Dataset, OncePer.Occurrence],
      subscribable: true,
    },
    [PlatformEventType.DatasetTagAdded]: {
      label: "Dataset tag added",
      subjectType: "dataset",
      roots: ["dataset", "tag"],
      defaultRoot: "dataset",
      oncePer: [OncePer.Occurrence],
      subscribable: true,
    },
    [PlatformEventType.InvocationCompleted]: {
      label: "Invocation completed",
      subjectType: "invocation",
      roots: ["action", "invocation"],
      defaultRoot: "invocation",
      oncePer: [OncePer.Occurrence, OncePer.Invocation],
      subscribable: true,
    },
    [PlatformEventType.InvocationFailed]: {
      label: "Invocation failed",
      subjectType: "invocation",
      roots: ["action", "invocation"],
      defaultRoot: "invocation",
      oncePer: [OncePer.Occurrence, OncePer.Invocation],
      subscribable: true,
    },
    [PlatformEventType.EventCreated]: {
      label: "Event created",
      subjectType: "event",
      roots: ["event"],
      defaultRoot: "event",
      oncePer: [OncePer.Event, OncePer.Occurrence],
      subscribable: true,
    },
    [PlatformEventType.ScheduleFired]: {
      label: "Schedule fired",
      subjectType: "trigger",
      roots: ["schedule"],
      defaultRoot: "schedule",
      oncePer: [OncePer.Occurrence],
      subscribable: false,
    },
  };

/** Every platform event type, in catalog order. */
export const ALL_PLATFORM_EVENT_TYPES = Object.keys(
  EVENT_CATALOG,
) as PlatformEventType[];

/** The platform event types a trigger may subscribe to: the picker's vocabulary. */
export const SUBSCRIBABLE_EVENT_TYPES = ALL_PLATFORM_EVENT_TYPES.filter(
  (eventType) => EVENT_CATALOG[eventType].subscribable,
);

/** Human label for a platform event type, falling back to the raw value for unknowns. */
export function eventLabel(eventType: PlatformEventType): string {
  return EVENT_CATALOG[eventType]?.label ?? eventType;
}

/**
 * The ``once_per`` choices legal for a trigger subscribed to ``events``: the
 * intersection of each platform event's supported set. Every platform event
 * supports ``occurrence``, so the result is never empty for a non-empty
 * subscription. An empty subscription yields every choice (nothing to
 * constrain yet).
 */
export function legalOncePerFor(
  events: readonly PlatformEventType[],
): OncePer[] {
  if (events.length === 0) {
    return Object.values(OncePer);
  }
  return Object.values(OncePer).filter((granularity) =>
    events.every((eventType) =>
      EVENT_CATALOG[eventType].oncePer.includes(granularity),
    ),
  );
}

/**
 * The namespace roots a condition may reference for a trigger subscribed to
 * ``events``: the intersection of each event's exposed roots. An empty
 * subscription yields no roots (there is nothing to condition on yet).
 */
export function variableRoots(events: readonly PlatformEventType[]): string[] {
  if (events.length === 0) {
    return [];
  }
  const [first, ...rest] = events;
  return EVENT_CATALOG[first].roots.filter((root) =>
    rest.every((eventType) => EVENT_CATALOG[eventType].roots.includes(root)),
  );
}

/**
 * The default root shared by every subscribed event, or ``null`` when the
 * subscription is empty or the events disagree (unqualified condition fields
 * would then be ambiguous, so the UI should require an explicit root).
 */
export function sharedDefaultRootFor(
  events: readonly PlatformEventType[],
): string | null {
  if (events.length === 0) {
    return null;
  }
  const first = EVENT_CATALOG[events[0]].defaultRoot;
  return events.every(
    (eventType) => EVENT_CATALOG[eventType].defaultRoot === first,
  )
    ? first
    : null;
}
